import { type EducationAnswer } from "@components/job_post/education_section";
import { type EducationConfiguration } from "@/types/jben/job_post";
import type { WhoamiProfile } from "@/queries/use_whoami";
import { type Option } from "@sprout/select";

export class EducationAutofiller {
  private answers: EducationAnswer[];
  private educationConfiguration: Partial<EducationConfiguration>;
  private setAnswers: (answers: EducationAnswer[]) => void;
  private educationApiUrl: string;

  constructor(
    answers: EducationAnswer[],
    setAnswers: (answers: EducationAnswer[]) => void,
    educationConfiguration?: EducationConfiguration,
    educationApiUrl = "/education",
  ) {
    this.answers = answers;
    this.setAnswers = setAnswers;
    this.educationConfiguration = educationConfiguration || {};
    this.educationApiUrl = educationApiUrl;
  }

  async fill(profile: WhoamiProfile): Promise<void> {
    if (!this.shouldFill) return;

    const newAnswers = await this.profileToAnswers(profile);
    if (newAnswers.length === 0) return;

    this.setAnswers(newAnswers);
  }

  private async profileToAnswers(profile: WhoamiProfile): Promise<EducationAnswer[]> {
    return await Promise.all(
      profile.educations?.map(async (education, index) => {
        const { degree, discipline, endMonth, endYear, school, startMonth, startYear } = education;

        const [schoolAnswer, degreeAnswer, disciplineAnswer] = await Promise.all([
          this.findOption("schools", school),
          this.findOption("degrees", degree),
          this.findOption("disciplines", discipline),
        ]);

        // Base the key on the length of the existing answers array to account
        // for cases where the user has already clicked the add another button.
        const key = this.answers.length + index;

        return {
          key,
          school: this.maybeAnswer("school_name", schoolAnswer),
          degree: this.maybeAnswer("degree", degreeAnswer),
          discipline: this.maybeAnswer("discipline", disciplineAnswer),
          start_month: this.maybeAnswer("start_month", this.findMonthOption(startMonth)),
          start_year: this.maybeAnswer("start_year", startYear),
          end_month: this.maybeAnswer("end_month", this.findMonthOption(endMonth)),
          end_year: this.maybeAnswer("end_year", endYear),
        };
      }) || [],
    );
  }

  // Fetches from the local /education endpoint and finds an exact label match.
  // The endpoint returns { items: [{ value: number, label: string }] } via
  // EducationPresenter, which is the Option format the AsyncSingleSelect expects.
  private async findOption(
    type: "schools" | "degrees" | "disciplines",
    name?: string | null,
  ): Promise<Option | undefined> {
    if (!name) return undefined;

    const response = await fetch(
      `${this.educationApiUrl}/${type}?term=${encodeURIComponent(name)}`,
      {
        headers: { Accept: "application/json" },
      },
    );

    if (!response.ok) return undefined;

    const {
      items,
    }: {
      items: ({ label: string; value: string | number } | { text: string; id: string | number })[];
    } = await response.json();

    const normalized: Option[] = items.map((item) =>
      "text" in item ? { label: item.text, value: item.id } : item,
    );

    return normalized.find((item) => item.label === name);
  }

  private findMonthOption(month?: number | null): Option | undefined {
    if (month == null) return undefined;
    const MONTH_LABELS = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return { value: month, label: MONTH_LABELS[month - 1] };
  }

  private maybeAnswer<T>(key: keyof EducationConfiguration, value?: T | null): T | undefined {
    if (!value || !this.fieldAvailable(key)) return undefined;
    return value;
  }

  private fieldAvailable(key: keyof EducationConfiguration): boolean {
    return (
      this.educationConfiguration[key] === "optional" ||
      this.educationConfiguration[key] === "required"
    );
  }

  private get shouldFill(): boolean {
    if (!this.hasEducationQuestions) return false;

    // If the user has manually provided ANY education answer, do not autofill
    return !this.answers.some((answer) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { key: _key, ...attributes } = answer;
      return Object.keys(attributes).some((key) => !!attributes[key as keyof typeof attributes]);
    });
  }

  private get hasEducationQuestions(): boolean {
    return (Object.keys(this.educationConfiguration) as (keyof EducationConfiguration)[]).some(
      (key) => this.fieldAvailable(key),
    );
  }
}
